import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import type { ReactNode } from 'react';

interface QuickActionCardProps {
  title: string;
  description?: string;
  to?: string;
  onClick?: () => void;
  icon: ReactNode;
  tone?: 'orange' | 'green' | 'navy' | 'purple' | 'blue';
  variant?: 'default' | 'compact';
}

const iconTones = {
  orange: 'bg-primary/10 text-primary group-hover:bg-primary/15',
  green: 'bg-secondary/10 text-secondary group-hover:bg-secondary/15',
  navy: 'bg-brand-navy/8 text-brand-navy group-hover:bg-brand-navy/12',
  purple: 'bg-violet-100 text-violet-600 group-hover:bg-violet-200/80',
  blue: 'bg-sky-100 text-sky-600 group-hover:bg-sky-200/80',
};

export function QuickActionCard({
  title,
  description,
  to,
  onClick,
  icon,
  tone = 'orange',
  variant = 'default',
}: QuickActionCardProps) {
  if (variant === 'compact') {
    const compactClass = cn(
      'group flex flex-col items-center justify-center rounded-xl border border-gray-100 bg-white p-2.5 text-center shadow-soft transition-all',
      'hover:border-gray-200 hover:shadow-card active:scale-[0.98]',
      'aspect-square sm:aspect-auto sm:flex-row sm:justify-start sm:gap-3 sm:p-3 sm:text-left',
      'lg:rounded-2xl lg:px-4 lg:py-3.5',
    );

    const content = (
      <>
        <div
          className={cn(
            'mb-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors sm:mb-0 lg:h-10 lg:w-10',
            iconTones[tone],
          )}
        >
          {icon}
        </div>
        <h3 className="text-[11px] font-semibold leading-tight text-brand-navy sm:text-sm">{title}</h3>
      </>
    );

    if (onClick) {
      return (
        <button type="button" onClick={onClick} className={compactClass}>
          {content}
        </button>
      );
    }

    return (
      <Link to={to!} className={compactClass}>
        {content}
      </Link>
    );
  }

  const className =
    'group flex flex-col rounded-2xl border border-gray-100 bg-white p-5 shadow-soft transition-all hover:border-primary/20 hover:shadow-card';

  const content = (
    <>
      <div className={cn('mb-4 inline-flex rounded-xl p-3', iconTones[tone])}>{icon}</div>
      <h3 className="font-bold text-brand-navy">{title}</h3>
      {description && <p className="mt-1 text-xs text-brand-muted">{description}</p>}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(className, 'text-left')}>
        {content}
      </button>
    );
  }

  return (
    <Link to={to!} className={className}>
      {content}
    </Link>
  );
}
